// Contact Card Component - Reusable contact display with expansion
import { t } from '../../../i18n.js';

export class ContactCard {
  constructor(wizard, contact) {
    this.wizard = wizard;
    this.contact = contact;
  }
  
  render() {
    const card = document.createElement('div');
    card.className = 'contact-card-wizard';
    
    const isSelected = this.wizard.selectedContacts.some(c => c._id === this.contact._id);
    if (isSelected) {
      card.classList.add('selected');
    }
    
    // Extract contact information
    const contactName = this.contact.value || t('unknown_contact');
    const companyName = Array.isArray(this.contact?.customers) && this.contact.customers.length > 0 
      ? this.contact.customers[0]?.value 
      : '';
    const primaryEmail = Array.isArray(this.contact?.emails) && this.contact.emails.length > 0 
      ? this.contact.emails[0] 
      : '';
    const primaryPhone = Array.isArray(this.contact?.phoneNumbers) && this.contact.phoneNumbers.length > 0 
      ? this.contact.phoneNumbers[0]?.number 
      : '';
    const leadCount = this.contact.leads?.length ?? 0;
    const campaignCount = this.contact.campaigns?.filter(campaign => !campaign.done).length ?? 0;
    
    card.innerHTML = `
      <div class="contact-card-content">
        <div class="contact-checkbox-wrapper">
          <input type="checkbox" class="contact-checkbox" value="${this.contact._id}" ${isSelected ? 'checked' : ''}>
        </div>
        <div class="contact-info" data-contact-id="${this.contact._id}">
          <div class="contact-name">${contactName}</div>
          ${companyName ? `<div class="contact-company">${companyName}</div>` : ''}
          <div class="contact-details">
            ${primaryEmail ? `<span class="contact-email">${primaryEmail}</span>` : ''}
            ${primaryPhone ? `<span class="contact-phone" title="${t('contact_click_copy')}"><span class="phone-display" data-phone="${primaryPhone}">📞 ${primaryPhone}</span></span>` : ''}
            ${this.contact.position ? `<span class="contact-position">${this.contact.position}</span>` : ''}
          </div>
        </div>
        <div class="contact-meta">
          <div class="contact-stats">
            <span class="stat-item">🎯 ${leadCount}</span>
            <span class="stat-item">📢 ${campaignCount}</span>
          </div>
          <button class="contact-expand-btn" title="${t('contact_view_details')}">ℹ️</button>
        </div>
      </div>
      ${this.renderExpandedDetails()}
    `;
    
    this.attachEventHandlers(card);
    return card;
  }
  
  renderExpandedDetails() {
    return `
      <div class="contact-expanded-details" style="display: none;">
        <div class="details-section">
          <h4>${t('contact_section_personal')}</h4>
          <div class="contact-detail-row">
            <strong>${t('contact_label_name')}</strong> ${this.contact?.value || t('not_applicable')}
          </div>
          <div class="contact-detail-row">
            <strong>${t('contact_label_position')}</strong> ${this.contact?.position || t('not_applicable')}
          </div>
          <div class="contact-detail-row">
            <strong>${t('contact_label_department')}</strong> ${this.contact?.department || t('not_applicable')}
          </div>
          <div class="contact-detail-row">
            <strong>${t('contact_label_salutation')}</strong> ${this.contact?.salutation || t('not_applicable')}
          </div>
          <div class="contact-detail-row">
            <strong>${t('contact_label_academic_title')}</strong> ${this.contact?.academicTitle || t('not_applicable')}
          </div>
        </div>
        
        <div class="details-section">
          <h4>${t('contact_section_contact')}</h4>
          <div class="contact-detail-row">
            <strong>${t('contact_label_emails')}</strong> ${this.contact?.emails?.join(', ') || t('not_applicable')}
          </div>
          <div class="contact-detail-row">
            <strong>${t('contact_label_phones')}</strong> ${this.contact?.phoneNumbers?.length ? 
              this.contact.phoneNumbers.map(p => `${p?.type || t('contact_phone_type')}: <span class="phone-display" data-phone="${p?.number || ''}" title="${t('contact_click_copy')}">${p?.number || ''}</span>`).join(', ') : t('not_applicable')}
          </div>
          <div class="contact-detail-row">
            <strong>${t('contact_label_linkedin')}</strong> ${this.contact?.linkedin ? 
              `<a href="${this.contact.linkedin}" target="_blank">${this.contact.linkedin}</a>` : t('not_applicable')}
          </div>
        </div>
        
        ${this.contact?.customers && this.contact.customers.length > 0 ? `
          <div class="details-section">
            <h4>${t('contact_section_company')}</h4>
            <div class="contact-detail-row">
              <strong>${t('contact_label_company')}</strong> ${this.contact.customers?.[0]?.value || t('not_applicable')}
            </div>
            <div class="contact-detail-row">
              <strong>${t('contact_label_branch')}</strong> ${this.contact.customers?.[0]?.branch || t('not_applicable')}
            </div>
            <div class="contact-detail-row">
              <strong>${t('contact_label_address')}</strong> ${this.contact.customers?.[0]?.mainLocation ? 
                `${this.contact.customers?.[0]?.mainLocation?.street}, ${this.contact.customers?.[0]?.mainLocation?.city}, ${this.contact.customers?.[0]?.mainLocation?.country}` : t('not_applicable')}
            </div>
          </div>
        ` : ''}
        
${this.contact?.activities && this.contact.activities.length > 0 ? this.renderActivitiesSection() : ''}
      </div>
    `;
  }

  renderActivitiesSection() {
    const sortedActivities = this.contact.activities
      .slice()
      .sort((a, b) => new Date(b?.startDate || 0) - new Date(a?.startDate || 0));
    
    const initialCount = 5;
    const hasMoreActivities = sortedActivities.length > initialCount;
    const initialActivities = sortedActivities.slice(0, initialCount);
    const remainingActivities = sortedActivities.slice(initialCount);

    return `
      <div class="details-section">
        <h4>${t('contact_recent_activities', { n: String(sortedActivities.length) })}</h4>
        <div class="activities-container" data-contact-id="${this.contact._id}">
          ${initialActivities.map(activity => this.renderActivityItem(activity)).join('')}
          ${hasMoreActivities ? `
            <div class="activities-hidden" style="display: none;">
              ${remainingActivities.map(activity => this.renderActivityItem(activity)).join('')}
            </div>
          ` : ''}
        </div>
        ${hasMoreActivities ? `
          <button class="show-more-activities-btn" data-contact-id="${this.contact._id}">
            ${t('contact_show_more_activities', { n: String(remainingActivities.length) })}
          </button>
        ` : ''}
      </div>
    `;
  }

  renderActivityItem(activity) {
    const subject = activity?.subject || t('contact_untitled');
    const actionType = activity?.actionType || t('not_applicable');
    const startDate = activity?.startDate ? new Date(activity.startDate).toLocaleDateString() : t('not_applicable');
    const activityId = activity?._id || '';

    return `
      <div class="activity-item ${activityId ? 'clickable' : ''}" 
           ${activityId ? `data-activity-id="${activityId}" title="${t('contact_activity_open_title')}"` : ''}>
        <div class="activity-header">
          <span class="activity-type">${actionType}</span>
          ${activityId ? '<span class="activity-link-icon">🔗</span>' : ''}
        </div>
        <div class="activity-subject">${subject}</div>
        <div class="activity-date">${startDate}</div>
      </div>
    `;
  }
  
  attachEventHandlers(card) {
    // Add click handler for checkbox selection and phone copy
    const contactInfo = card.querySelector('.contact-info');
    contactInfo.addEventListener('click', (e) => {
      if (e.target.classList.contains('phone-display')) {
        // Handle phone number copy
        const phoneNumber = e.target.getAttribute('data-phone');
        this.wizard.copyToClipboard(phoneNumber);
        e.stopPropagation();
        return;
      }
      
      if (!e.target.closest('.contact-expand-btn')) {
        const checkbox = card.querySelector('.contact-checkbox');
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change'));
      }
    });
    
    // Add expand button handler
    const expandBtn = card.querySelector('.contact-expand-btn');
    const expandedDetails = card.querySelector('.contact-expanded-details');
    
    expandBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isExpanded = expandedDetails.style.display !== 'none';
      
      if (isExpanded) {
        expandedDetails.style.display = 'none';
        expandBtn.textContent = 'ℹ️';
        expandBtn.title = t('contact_view_details');
      } else {
        expandedDetails.style.display = 'block';
        expandBtn.textContent = '❌';
        expandBtn.title = t('contact_hide_details');
      }
    });
    
    // Add click handler for phone numbers and activities in expanded details
    expandedDetails.addEventListener('click', (e) => {
      if (e.target.classList.contains('phone-display')) {
        const phoneNumber = e.target.getAttribute('data-phone');
        this.wizard.copyToClipboard(phoneNumber);
        e.stopPropagation();
        return;
      }

      // Handle "Show More" button clicks
      if (e.target.classList.contains('show-more-activities-btn')) {
        e.stopPropagation();
        this.handleShowMoreActivities(e.target);
        return;
      }

      // Handle activity item clicks
      const activityItem = e.target.closest('.activity-item.clickable');
      if (activityItem) {
        e.stopPropagation();
        const activityId = activityItem.getAttribute('data-activity-id');
        if (activityId) {
          this.openActivityInNewTab(activityId);
        }
        return;
      }
    });
    
    // Add checkbox change handler
    const checkbox = card.querySelector('.contact-checkbox');
    checkbox.addEventListener('change', (e) => {
      if (e.target.checked) {
        if (!this.wizard.selectedContacts.some(c => c._id === this.contact._id)) {
          this.wizard.selectedContacts.push(this.contact);
        }
        card.classList.add('selected');
      } else {
        this.wizard.selectedContacts = this.wizard.selectedContacts.filter(c => c._id !== this.contact._id);
        card.classList.remove('selected');
      }
      
      // Update navigation
      this.wizard.updateNavigation();
      
      // Update contacts summary if we're in consolidated view
      if (this.wizard.steps[1] && this.wizard.steps[1].refreshContactsSummary) {
        this.wizard.steps[1].refreshContactsSummary();
      }
    });
  }

  // Handle "Show More" activities button
  handleShowMoreActivities(button) {
    const contactId = button.getAttribute('data-contact-id');
    const activitiesContainer = document.querySelector(`.activities-container[data-contact-id="${contactId}"]`);
    const hiddenActivities = activitiesContainer.querySelector('.activities-hidden');
    
    if (hiddenActivities) {
      const isHidden = hiddenActivities.style.display === 'none';
      
      if (isHidden) {
        // Show hidden activities
        hiddenActivities.style.display = 'block';
        button.textContent = t('contact_show_less');
      } else {
        // Hide activities
        hiddenActivities.style.display = 'none';
        const remainingCount = hiddenActivities.querySelectorAll('.activity-item').length;
        button.textContent = t('contact_show_more_activities', { n: String(remainingCount) });
      }
    }
  }

  // Open activity in new tab
  async openActivityInNewTab(activityId) {
    try {
      // Get the instance from auth service
      const { getInstance } = await this.wizard.getAuthInstance();
      const instance = getInstance();
      
      if (!instance) {
        console.error('No instance found for opening activity');
        return;
      }

      // Construct the activity URL with /details
      const activityUrl = `https://${instance}.assistgo.de/activity/${activityId}/details`;
      
      // Open in new tab
      window.open(activityUrl, '_blank', 'noopener,noreferrer');
      
    } catch (error) {
      console.error('Failed to open activity:', error);
      // Fallback: try to open without instance (might not work but worth trying)
      const fallbackUrl = `https://dev.assistgo.de/activity/${activityId}/details`;
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
  }
}
